import { isNil } from 'lodash';
import { AbortError } from 'shared/errors';
import { transformPoint } from 'shared/helpers/crs-helpers';
import { convertRgb16To8 } from 'shared/utils';
import type {
  BoundingBox3D,
  CopcReader,
  CrossSectionOptions,
  CrossSectionPoint,
  Point2D,
} from '../types';
import { GeometryUtils } from './geometry-utils';
import {
  checkAborted,
  getErrorMessage,
  preprocessInputsForMetricCalculations,
  reportProgress,
} from './helpers';

/**
 * Cross-section extractor for COPC point cloud files.
 *
 * Extracts cross-section points from a COPC file using a reader provided by the caller.
 * The tool manages reader lifecycle; the extractor is stateless per extraction.
 */
export class CrossSectionExtractor {
  private copcFile: string;

  constructor(copcFile: string) {
    this.copcFile = copcFile;
  }

  /**
   * Extracts cross-section points along a path with a buffer distance.
   *
   * Creates a corridor along the specified path and extracts all point cloud
   * points within that corridor. Each point includes its position, color (if available),
   * and distance along the path (mileage).
   *
   * The extraction process:
   * 1. Preprocesses inputs to handle geographic CRS (converts to UTM for metric calculations)
   * 2. Divides the path into segments between consecutive points
   * 3. For each segment, creates a tight bounding box with the buffer distance
   * 4. Queries the COPC octree for points within each bounding box (in original CRS)
   * 5. Filters points by perpendicular distance to the line segment (in meters)
   * 6. Calculates mileage (distance along path) for each point (in meters)
   *
   * Progress reporting:
   * - Reports overall progress (0-100) via onProgress callback
   * - Progress is segment-based with sub-progress for node loading
   *
   * Performance:
   * - File remains open between extractions for efficiency
   * - Only queries relevant octree nodes (spatial indexing)
   * - Supports cancellation at multiple points via AbortSignal
   * - Handles geographic CRS (WGS84) by converting to UTM for calculations
   */
  async extractCrossSection(
    reader: CopcReader,
    path: Point2D[],
    buffer: number,
    targetCrs: number,
    options?: CrossSectionOptions,
  ): Promise<CrossSectionPoint[]> {
    const { onProgress, signal } = options || {};

    if (isNil(path) || path.length < 2) {
      throw new Error('Path must contain at least 2 points');
    }
    if (buffer < 0) {
      throw new Error('Buffer must be a positive number');
    }

    checkAborted(signal);

    // Get bounds from reader.
    const header = reader.getHeader();
    const bounds: BoundingBox3D = {
      minX: header.min.x,
      maxX: header.max.x,
      minY: header.min.y,
      maxY: header.max.y,
      minZ: header.min.z,
      maxZ: header.max.z,
    };

    const preprocessed = preprocessInputsForMetricCalculations(
      path,
      buffer,
      targetCrs,
    );
    const { pathInMeters, bufferInMeters, isProjected, utmContext } =
      preprocessed;

    // Buffer is the total width of the corridor, so we need to divide it by 2 to get the buffer on each side.
    const bufferOnEachSide = bufferInMeters / 2;

    // Calculate cumulative distances using path in meters.
    const cumulativeDistances = pathInMeters.slice(0, -1).reduce<number[]>(
      (acc, currentPoint, index) => {
        const segmentLength = GeometryUtils.distance2D(
          currentPoint,
          pathInMeters[index + 1],
        );
        const previousDistance = acc[acc.length - 1] ?? 0;
        acc.push(previousDistance + segmentLength);
        return acc;
      },
      [0.0],
    );

    const allPoints: CrossSectionPoint[] = [];
    const totalSegments = pathInMeters.length - 1;

    for (let i = 0; i < totalSegments; i++) {
      checkAborted(signal);

      // Use path in meters for all geometric calculations.
      const segmentStart = pathInMeters[i];
      const segmentEnd = pathInMeters[i + 1];
      const segmentCumulativeStart = cumulativeDistances[i];

      // Calculating bbox in meters.
      const [minX, minY, maxX, maxY] =
        GeometryUtils.calculateTightBboxForSegment(
          segmentStart,
          segmentEnd,
          bufferOnEachSide,
        );

      // If target CRS is geographic, transforming bbox back to degrees.
      let queryMinX = minX;
      let queryMinY = minY;
      let queryMaxX = maxX;
      let queryMaxY = maxY;

      if (!isProjected && utmContext) {
        const { epsgCode, zoneNumber, hemisphere } = utmContext;

        const cornersInMeters: Point2D[] = [
          [minX, minY],
          [maxX, minY],
          [minX, maxY],
          [maxX, maxY],
        ];

        const transformed = cornersInMeters.map((corner) =>
          transformPoint(corner, epsgCode, zoneNumber, hemisphere, false),
        );

        const xs = transformed.map(([x]) => x);
        const ys = transformed.map(([, y]) => y);

        queryMinX = Math.min(...xs);
        queryMaxX = Math.max(...xs);
        queryMinY = Math.min(...ys);
        queryMaxY = Math.max(...ys);
      }

      // Clip to COPC bounds.
      const clippedMinX = Math.max(queryMinX, bounds.minX);
      const clippedMaxX = Math.min(queryMaxX, bounds.maxX);
      const clippedMinY = Math.max(queryMinY, bounds.minY);
      const clippedMaxY = Math.min(queryMaxY, bounds.maxY);

      if (clippedMinX >= clippedMaxX || clippedMinY >= clippedMaxY) {
        reportProgress(i, totalSegments, onProgress);
        continue;
      }

      try {
        const segmentStartPercent = (i / totalSegments) * 100;
        const segmentEndPercent = ((i + 1) / totalSegments) * 100;
        const segmentProgressRange = segmentEndPercent - segmentStartPercent;

        const nodeProgressCallback = !isNil(onProgress)
          ? (nodeCurrent: number, nodeTotal: number) => {
              if (nodeTotal > 0) {
                const nodeProgress = nodeCurrent / nodeTotal;
                const overallProgress =
                  segmentStartPercent + nodeProgress * segmentProgressRange;
                onProgress(
                  Math.min(100, Math.max(0, Math.round(overallProgress))),
                );
              }
            }
          : undefined;

        const queryBbox: BoundingBox3D = {
          minX: clippedMinX,
          minY: clippedMinY,
          minZ: bounds.minZ,
          maxX: clippedMaxX,
          maxY: clippedMaxY,
          maxZ: bounds.maxZ,
        };

        // Querying COPC with bbox in target CRS.
        const points = await reader.getPointsWithinBox(
          queryBbox,
          signal,
          nodeProgressCallback,
        );
        checkAborted(signal);

        for (const point of points) {
          checkAborted(signal);

          // Transforming point to meters if needed.
          let pointInMeters: Point2D = [point.x, point.y];

          if (!isProjected && utmContext) {
            pointInMeters = transformPoint(
              [point.x, point.y],
              utmContext.epsgCode,
              utmContext.zoneNumber,
              utmContext.hemisphere,
              true,
            );
          }

          const [perpendicularDistance, projectedDistance] =
            GeometryUtils.pointToLineDistance(
              pointInMeters,
              segmentStart,
              segmentEnd,
            );

          if (perpendicularDistance <= bufferOnEachSide) {
            const mileage = segmentCumulativeStart + projectedDistance;
            const { r, g, b } = convertRgb16To8(
              point.red,
              point.green,
              point.blue,
            );
            const intensity = !isNil(point.intensity)
              ? Math.floor(point.intensity)
              : 0;

            // Storing the points in COPC CRS.
            allPoints.push({
              x: point.x,
              y: point.y,
              z: point.z,
              r,
              g,
              b,
              intensity,
              mileage,
            });
          }
        }
      } catch (error: unknown) {
        if (error instanceof AbortError) {
          throw error;
        }
        console.error(
          `Error querying segment ${i + 1}/${totalSegments}: ${getErrorMessage(error)}`,
        );
      }

      reportProgress(i, totalSegments, onProgress);
    }

    return allPoints;
  }
}
